/**
 * <softness-finder>
 *
 * A draggable softness scale that narrows a pre-rendered product grid down to
 * the mattresses whose `S<NN>` tag sits within `data-tolerance` of the chosen
 * value, closest match first, capped at `data-max-results` (or
 * `data-max-results-mobile` below the 750px breakpoint). The Expand button
 * lifts the cap while keeping the softness filter.
 *
 * The grid swaps with a leave/enter transition rather than snapping. While the
 * transition runs, further scale movement is queued and applied from the live
 * value once it finishes — so a drag never stacks up animations.
 */
const SF_LEAVE_MS = 130;
const SF_ENTER_MS = 240;
const SF_INTRO_MS = 950;

class SoftnessFinder extends HTMLElement {
  connectedCallback() {
    this.range = this.querySelector('[data-sf-range]');
    this.grid = this.querySelector('[data-sf-grid]');
    if (!this.range || !this.grid) return;

    this.slider = this.querySelector('[data-sf-slider]');
    this.marker = this.querySelector('[data-sf-marker]');
    this.valueOutput = this.querySelector('[data-sf-value]');
    this.levelOutput = this.querySelector('[data-sf-level]');
    this.countOutput = this.querySelector('[data-sf-count]');
    this.emptyMessage = this.querySelector('[data-sf-empty]');
    this.toggleButton = this.querySelector('[data-sf-toggle]');
    this.toggleText = this.querySelector('[data-sf-toggle-text]');
    this.actions = this.querySelector('[data-sf-actions]');
    this.collectionLink = this.querySelector('[data-sf-link]');
    this.hintCaption = this.querySelector('[data-sf-hint]');
    this.items = Array.from(this.querySelectorAll('[data-sf-item]'));
    this.positions = new Map(this.items.map((item, index) => [item, index]));

    this.tolerance = this.numberFrom(this.dataset.tolerance, 15);
    this.maxResults = this.numberFrom(this.dataset.maxResults, 0);
    this.maxResultsMobile = this.numberFrom(this.dataset.maxResultsMobile, this.maxResults);
    this.mobileQuery = window.matchMedia ? window.matchMedia('(max-width: 749px)') : null;
    this.levels = this.parseJSON('[data-sf-levels]', []);
    this.strings = this.parseJSON('[data-sf-strings]', {});

    this.expanded = false;
    this.animating = false;
    this.updateQueued = false;
    this.signature = null;
    this.hintRetired = false;
    this.introFrame = null;

    this.range.addEventListener('input', this.onInput.bind(this));
    this.range.addEventListener('pointerdown', () => {
      this.retireHint();
      this.setDragging(true);
    });
    ['keydown', 'focus'].forEach((event) => {
      this.range.addEventListener(event, () => this.retireHint());
    });
    ['pointerup', 'pointercancel', 'blur'].forEach((event) => {
      this.range.addEventListener(event, () => this.setDragging(false));
    });

    if (this.toggleButton) this.toggleButton.addEventListener('click', this.onToggle.bind(this));

    // Crossing the breakpoint changes how many cards fit in a row, so the cap
    // moves with it.
    if (this.mobileQuery) {
      const onBreakpoint = () => this.applyPlan(true);
      if (this.mobileQuery.addEventListener) this.mobileQuery.addEventListener('change', onBreakpoint);
      else if (this.mobileQuery.addListener) this.mobileQuery.addListener(onBreakpoint);
    }

    this.renderScale();
    this.applyPlan(false);
    this.watchForIntro();
  }

  /* --- Drag affordance --------------------------------------------------
   * The range is invisible, so on its own the scale reads as decoration. When
   * the panel first scrolls into view the thumb sweeps in from the far end and
   * settles on the starting value, with the number counting along — it shows
   * the handle travelling, and lands on the value the results already reflect,
   * so nothing on screen contradicts anything else. Breathing chevrons and a
   * caption then linger until the shopper takes over.
   */

  watchForIntro() {
    if (typeof IntersectionObserver !== 'function') {
      this.startIntro();
      return;
    }

    this.introObserver = new IntersectionObserver(
      (entries) => {
        if (!entries[0].isIntersecting) return;
        this.introObserver.disconnect();
        this.startIntro();
      },
      { threshold: 0.4 }
    );

    this.introObserver.observe(this.slider || this);
  }

  startIntro() {
    if (this.hintRetired) return;

    // No sweep when motion is unwelcome — the caption alone carries the message.
    if (this.prefersReducedMotion) {
      this.showHint();
      return;
    }

    const target = this.percentFor(this.value);
    const from = target <= 10 ? 100 : 0;
    const started = performance.now();

    if (this.slider) this.slider.classList.add('softness-finder__slider--intro');

    const step = (now) => {
      if (this.hintRetired) return;

      const t = Math.min((now - started) / SF_INTRO_MS, 1);
      // easeOutBack: overshoots a touch and settles, the way a real handle would.
      const c1 = 1.70158;
      const c3 = c1 + 1;
      const eased = 1 + c3 * Math.pow(t - 1, 3) + c1 * Math.pow(t - 1, 2);
      const percent = from + (target - from) * eased;

      this.paintScale(percent, this.valueForPercent(percent));

      if (t < 1) {
        this.introFrame = requestAnimationFrame(step);
      } else {
        this.endIntro();
      }
    };

    this.introFrame = requestAnimationFrame(step);
  }

  endIntro() {
    this.introFrame = null;
    if (this.slider) this.slider.classList.remove('softness-finder__slider--intro');
    this.renderScale();
    this.showHint();
  }

  showHint() {
    if (this.hintRetired) return;
    this.classList.add('softness-finder--hint');
  }

  /** First real interaction — stop hinting, for good. */
  retireHint() {
    if (this.hintRetired) return;
    this.hintRetired = true;

    if (this.introFrame) {
      cancelAnimationFrame(this.introFrame);
      this.introFrame = null;
    }
    if (this.introObserver) this.introObserver.disconnect();
    if (this.slider) this.slider.classList.remove('softness-finder__slider--intro');
    this.classList.remove('softness-finder--hint');
    this.renderScale();
  }

  numberFrom(raw, fallback) {
    const parsed = parseInt(raw, 10);
    return Number.isNaN(parsed) ? fallback : parsed;
  }

  parseJSON(selector, fallback) {
    const node = this.querySelector(selector);
    if (!node) return fallback;
    try {
      return JSON.parse(node.textContent);
    } catch (error) {
      return fallback;
    }
  }

  get value() {
    return parseInt(this.range.value, 10);
  }

  /** How many matches to show before Expand, for the current viewport. */
  get cap() {
    return this.mobileQuery && this.mobileQuery.matches ? this.maxResultsMobile : this.maxResults;
  }

  get prefersReducedMotion() {
    return Boolean(window.matchMedia && window.matchMedia('(prefers-reduced-motion: reduce)').matches);
  }

  setDragging(isDragging) {
    if (this.slider) this.slider.classList.toggle('softness-finder__slider--dragging', isDragging);
  }

  onInput() {
    // Any interaction with the scale collapses the grid back to matches.
    this.retireHint();
    this.expanded = false;
    this.renderScale();
    this.applyPlan(true);
  }

  onToggle() {
    this.retireHint();
    this.expanded = !this.expanded;
    this.applyPlan(true);
  }

  /** Descriptor ("Medium Firm", …) for a value, from the ranges Liquid passed in. */
  levelFor(value) {
    const match = this.levels.find((level) => value <= level.max);
    return match ? match.label : '';
  }

  get bounds() {
    return {
      min: parseFloat(this.range.min) || 0,
      max: parseFloat(this.range.max) || 100,
      step: parseFloat(this.range.step) || 1,
    };
  }

  percentFor(value) {
    const { min, max } = this.bounds;
    return max === min ? 0 : ((value - min) / (max - min)) * 100;
  }

  /** Percent back to a real, step-aligned value — used by the intro sweep. */
  valueForPercent(percent) {
    const { min, max, step } = this.bounds;
    const raw = min + (percent / 100) * (max - min);
    return Math.min(max, Math.max(min, Math.round(raw / step) * step));
  }

  /** Paint the scale at an arbitrary position, without touching the input. */
  paintScale(percent, value) {
    this.style.setProperty('--sf-pct', percent);
    if (this.valueOutput) this.valueOutput.textContent = value;
    if (this.levelOutput) this.levelOutput.textContent = this.levelFor(value);

    if (this.marker) {
      this.marker.classList.toggle('softness-finder__marker--start', percent <= 15);
      this.marker.classList.toggle('softness-finder__marker--end', percent >= 85);
    }
  }

  /** Scale visuals track the drag directly — only the grid is animated. */
  renderScale() {
    const value = this.value;
    this.paintScale(this.percentFor(value), value);
    this.range.setAttribute('aria-valuetext', `${value} — ${this.levelFor(value)}`);
  }

  /**
   * Which products should be on show, in which order, for the current state.
   * Expanding lifts the cap — it never drops the softness filter, so the
   * shopper's choice survives the click. "Browse all mattresses" is the way
   * out to the unfiltered collection.
   */
  buildPlan() {
    const value = this.value;
    const matches = [];

    this.items.forEach((item) => {
      const softness = parseInt(item.dataset.softness, 10);
      if (Number.isNaN(softness) || softness < 0 || softness > 100) return;
      const distance = Math.abs(softness - value);
      if (distance <= this.tolerance) matches.push({ item, distance });
    });

    // Stable sort keeps the collection's own order as the tie-breaker, so
    // equally close mattresses stay in merchandised order.
    matches.sort((a, b) => a.distance - b.distance);

    // More matches than the cap means Expand has something to reveal.
    const cap = this.cap;
    const expandable = cap > 0 && matches.length > cap;
    const capped = expandable && !this.expanded;
    const shown = capped ? matches.slice(0, cap) : matches;

    return {
      items: shown.map((match) => match.item),
      matched: matches.length,
      capped,
      expandable,
      expanded: this.expanded,
    };
  }

  signatureFor(plan) {
    return `${plan.expanded}|${plan.matched}|${plan.items.map((item) => this.positions.get(item)).join(',')}`;
  }

  applyPlan(animate) {
    const plan = this.buildPlan();
    const signature = this.signatureFor(plan);
    if (signature === this.signature) return;

    if (!animate || this.prefersReducedMotion) {
      this.signature = signature;
      this.commit(plan);
      return;
    }

    // Mid-flight: remember that something changed and re-derive on landing, so
    // a continuous drag always settles on the value the shopper stopped at.
    if (this.animating) {
      this.updateQueued = true;
      return;
    }

    this.signature = signature;
    this.animating = true;

    const startHeight = this.grid.offsetHeight;
    this.grid.classList.add('softness-finder__grid--leaving');

    setTimeout(() => {
      const fresh = this.buildPlan();
      this.signature = this.signatureFor(fresh);

      this.grid.classList.remove('softness-finder__grid--leaving');
      this.grid.classList.add('softness-finder__grid--entering');
      this.commit(fresh);

      const endHeight = this.grid.offsetHeight;
      if (startHeight !== endHeight) {
        this.grid.classList.add('softness-finder__grid--resizing');
        this.grid.style.height = `${startHeight}px`;
        void this.grid.offsetHeight;
        this.grid.style.height = `${endHeight}px`;
      }

      // A frame later the start state is dropped and the cards ease in.
      requestAnimationFrame(() => {
        requestAnimationFrame(() => this.grid.classList.remove('softness-finder__grid--entering'));
      });

      setTimeout(() => {
        this.grid.classList.remove('softness-finder__grid--resizing');
        this.grid.style.height = '';
        this.animating = false;

        if (this.updateQueued) {
          this.updateQueued = false;
          this.applyPlan(true);
        }
      }, SF_ENTER_MS);
    }, SF_LEAVE_MS);
  }

  commit(plan) {
    const visible = new Set(plan.items);

    this.items.forEach((item) => {
      if (visible.has(item)) return;
      item.hidden = true;
      item.classList.add('softness-finder__item--hidden');
      item.style.order = '';
      item.style.removeProperty('--sf-index');
    });

    plan.items.forEach((item, index) => {
      item.hidden = false;
      item.classList.remove('softness-finder__item--hidden');
      item.style.order = index;
      item.style.setProperty('--sf-index', index);
    });

    this.renderStatus(plan);
  }

  renderStatus(plan) {
    const visible = plan.items.length;

    if (this.emptyMessage) this.emptyMessage.hidden = visible !== 0;

    if (this.countOutput) {
      let template = '';
      if (plan.capped) {
        template = this.strings.count_capped;
      } else if (plan.expanded && plan.expandable) {
        template = this.strings.count_expanded;
      } else if (visible === 1) {
        template = this.strings.count_one;
      } else if (visible > 1) {
        template = this.strings.count_other;
      }

      this.countOutput.textContent = (template || '')
        .replace('{{ count }}', visible)
        .replace('{{ total }}', plan.matched);
    }

    if (this.toggleButton) {
      // Nothing left to reveal — don't offer an Expand that does nothing.
      this.toggleButton.hidden = !plan.expandable;
      this.toggleButton.setAttribute('aria-expanded', String(plan.expanded));
      this.toggleButton.classList.toggle('softness-finder__button--expanded', plan.expanded);
      if (this.toggleText) {
        this.toggleText.textContent = plan.expanded ? this.strings.collapse : this.strings.expand;
      }
    }

    // Don't leave an empty divider bar when neither button is on show.
    if (this.actions) {
      const hasAction = (this.toggleButton && !this.toggleButton.hidden) || Boolean(this.collectionLink);
      this.actions.hidden = !hasAction;
    }
  }
}

customElements.define('softness-finder', SoftnessFinder);
